import React, { MouseEventHandler, useEffect, useState } from 'react';
import { ImageOptionalLink } from '../../../commonTypes/links';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import NavDesktop from '../NavDesktop';
import NavMobile from '../NavMobile';
import SimpleSecondaryNavMobile from '../NavMobile/SimpleSecondaryNav';
import { PrimaryNav, SecondaryNav, SimpleSecondaryNav } from '../types';
import { ContentOverlay, HeaderInner, HeaderOuter, Logo, MobileLogo } from './styles';

export type HeaderProps = {
  logoLink: ImageOptionalLink;
  mobileLogoLink?: ImageOptionalLink;
  primaryNav?: PrimaryNav;
  secondaryNav?: SecondaryNav;
  simpleSecondaryNav?: SimpleSecondaryNav;
  toggleMenuCallback?: (toggled: boolean) => void;
  mobileMenuOnOpen?: MouseEventHandler;
  mobileMenuOnClose?: MouseEventHandler;
  isFullWidth?: boolean;
  isDifferentBackgroundInMobile?: boolean;
  showArrowsOnPrimaryNav?: boolean;
  headerText?: string;
  headerCloseText?: string;
  baseIdPrefix?: string;
  dummyColumSectionAtEnd?: boolean;
} & ComponentProps;

const Header: React.FC<HeaderProps> = ({
  logoLink,
  primaryNav,
  secondaryNav,
  toggleMenuCallback,
  mobileMenuOnOpen,
  mobileMenuOnClose,
  simpleSecondaryNav,
  isFullWidth = false,
  isDifferentBackgroundInMobile = false,
  showArrowsOnPrimaryNav = false,
  mobileLogoLink,
  baseIdPrefix = '',
  headerText,
  headerCloseText,
  dummyColumSectionAtEnd = false,
  ...props
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeNavItemIndex, setActiveNavItemIndex] = useState<number | null>(null);

  useEffect(() => {
    toggleMenuCallback?.(activeNavItemIndex !== null);
  }, [activeNavItemIndex, toggleMenuCallback]);

  const resetNav = (): void => {
    setActiveNavItemIndex(null);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <HeaderOuter {...componentProps(props)} aria-label="main-header">
        {simpleSecondaryNav && (
          <SimpleSecondaryNavMobile secondaryNav={simpleSecondaryNav} />
        )}
        <HeaderInner
          aria-label="main-nav"
          isFullWidth={isFullWidth}
          isDifferentBackgroundInMobile={isDifferentBackgroundInMobile}>
          <Logo
            logoLink={logoLink}
            background="light"
            shouldEagerLoad={true}
            isMobileLogo={!!mobileLogoLink}
          />
          {mobileLogoLink && (
            <MobileLogo
              logoLink={mobileLogoLink}
              background="light"
              shouldEagerLoad={true}
            />
          )}
          {/* 'Mobile' nav is the burger-style nav, active for mobile and portrait tablet breakpoints. */}
          <NavMobile
            primaryNav={primaryNav}
            secondaryNav={secondaryNav}
            menuOpen={mobileMenuOpen}
            toggleMenuOpen={() => setMobileMenuOpen((state) => !state)}
            activeItemIndex={activeNavItemIndex}
            setActiveItemIndex={setActiveNavItemIndex}
            onOpen={mobileMenuOnOpen}
            onClose={mobileMenuOnClose}
            resetNav={resetNav}
            headerText={headerText}
            headerCloseText={headerCloseText}
            baseIdPrefix={baseIdPrefix}
          />
          {/* 'Desktop' nav is the inline nav, active for desktop and landscape tablet breakpoints. */}
          <NavDesktop
            primaryNav={primaryNav}
            secondaryNav={secondaryNav}
            simpleSecondaryNav={simpleSecondaryNav}
            activeItemIndex={activeNavItemIndex}
            setActiveItemIndex={setActiveNavItemIndex}
            resetNav={resetNav}
            showArrowsOnPrimaryNav={showArrowsOnPrimaryNav}
            dummyColumSectionAtEnd={dummyColumSectionAtEnd}
          />
        </HeaderInner>
      </HeaderOuter>
      <ContentOverlay
        activeMobile={mobileMenuOpen}
        activeDesktop={activeNavItemIndex !== null}
        onClick={resetNav}
      />
    </>
  );
};

export default applyComponentTheme(Header, 'Header');
